import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { LoginCredentials, AuthResponse, ApiResponse, Message, Contact } from '../types';
import { getAuthToken, clearAuthData } from '../utils/auth';

// API base URL - will be configurable via environment variables
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

class APIService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle common errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Unauthorized - clear all auth data and redirect to login
          clearAuthData();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication methods
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> = await this.api.post('/auth/login', credentials);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed',
      };
    }
  }

  async logout(): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.post('/auth/logout');
      // Clear all auth data
      clearAuthData();
      return response.data;
    } catch (error: any) {
      // Always clear storage even if API call fails
      clearAuthData();
      return {
        success: false,
        message: error.response?.data?.message || 'Logout failed',
      };
    }
  }

  async getCurrentUser(): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.get('/auth/me');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get user info',
      };
    }
  }

  // Message methods
  async getMessages(page: number = 1, limit: number = 30): Promise<ApiResponse<{messages: Message[], total: number}>> {
    try {
      console.log(`🌐 API: Fetching messages with page=${page} and limit=${limit}`);
      console.log('🌐 API: Making request to:', `/messages?page=${page}&limit=${limit}`);
      console.log('🌐 API: Full URL will be:', `${this.api.defaults.baseURL}/messages?page=${page}&limit=${limit}`);
      
      const response: AxiosResponse<ApiResponse<{messages: Message[], total: number}>> = 
        await this.api.get(`/messages?page=${page}&limit=${limit}`);
      
      console.log('🌐 API: Response status:', response.status);
      console.log('🌐 API: Response headers:', response.headers);
      console.log('🌐 API: Response data:', response.data);
      console.log('🌐 API: Response success:', response.data?.success);
      
      return response.data;
    } catch (error: any) {
      console.error('💥 API: Error fetching messages:', error);
      console.error('💥 API: Error response:', error.response);
      console.error('💥 API: Error status:', error.response?.status);
      console.error('💥 API: Error data:', error.response?.data);
      
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch messages',
      };
    }
  }

  async getMessagesByPhone(phoneNumber: string, limit: number = 50, offset: number = 0): Promise<ApiResponse<{ messages: Message[]; total: number }>> {
    try {
      const response: AxiosResponse<ApiResponse<{ messages: Message[]; total: number }>> = await this.api.get(
        `/messages/phone/${phoneNumber}?limit=${limit}&offset=${offset}`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch messages',
      };
    }
  }

  async sendMessage(phoneNumber: string, message: string): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.post('/messages/send', {
        phoneNumber,
        message,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send message',
      };
    }
  }

  // Contact methods
  async getContacts(): Promise<ApiResponse<Contact[]>> {
    try {
      const response: AxiosResponse<ApiResponse<Contact[]>> = await this.api.get('/contacts');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch contacts',
      };
    }
  }

  async createContact(contactData: Partial<Contact>): Promise<ApiResponse<Contact>> {
    try {
      const response: AxiosResponse<ApiResponse<Contact>> = await this.api.post('/contacts', contactData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create contact',
      };
    }
  }

  async updateContact(contactId: number, contactData: Partial<Contact>): Promise<ApiResponse<Contact>> {
    try {
      const response: AxiosResponse<ApiResponse<Contact>> = await this.api.put(`/contacts/${contactId}`, contactData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update contact',
      };
    }
  }

  // Media methods
  async uploadMedia(file: File): Promise<ApiResponse<any>> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response: AxiosResponse<ApiResponse> = await this.api.post('/media/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to upload media',
      };
    }
  }
  
  // Delete messages
  async deleteMessages(messageIds: string[]): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.delete('/messages', {
        data: { messageIds }
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete messages',
      };
    }
  }
}

// Export singleton instance
export const apiService = new APIService();
export default apiService;